import React, {useState, useEffect} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resendVerificationEmail } from '../../lib/api';

import { useUserContext } from "../../contexts";
import '../../styles/layout.scss';
import './EmailVerify.scss';
import { Header } from '../Header';
import { Spinner } from '../Spinner';
import {ButtonCTA} from '../Form';

const buildSubscribeToQuerySuffix = (subscribeTo) => {
    const params = new URLSearchParams();
    const addonPriceIds = Array.isArray(subscribeTo?.addon_price_ids)
        ? subscribeTo.addon_price_ids
        : (typeof subscribeTo?.addon_price_ids === 'string'
            ? subscribeTo.addon_price_ids.split(',').map((item) => Number(item.trim())).filter((id) => Number.isFinite(id) && id > 0)
            : (Array.isArray(subscribeTo?.addon_ids)
                ? subscribeTo.addon_ids
                : (typeof subscribeTo?.addon_ids === 'string'
                    ? subscribeTo.addon_ids.split(',').map((item) => Number(item.trim())).filter((id) => Number.isFinite(id) && id > 0)
                    : []))
        );
    if(!Array.isArray(addonPriceIds)){
        return '';
    }
    const uniqueAddonPriceIds = addonPriceIds
        .map((id) => Number(id))
        .filter((id, index, source) => Number.isFinite(id) && id > 0 && source.indexOf(id) === index);
    if(subscribeTo?.campaign){
        params.set('c', subscribeTo.campaign);
    }
    if(subscribeTo?.payment_link_id){
        params.set('payment_link_id', subscribeTo.payment_link_id);
    }
    if (subscribeTo?.checkout_item_type) {
        params.set('checkout_item_type', subscribeTo.checkout_item_type);
    }
    const bundleLicenseTierId = Number(subscribeTo?.bundle_license_tier_id);
    if (Number.isFinite(bundleLicenseTierId) && bundleLicenseTierId > 0) {
        params.set('bundle_license_tier_id', String(bundleLicenseTierId));
    }
    if (typeof subscribeTo?.redirect_url === 'string' && subscribeTo.redirect_url.trim()) {
        params.set('redirect_url', subscribeTo.redirect_url.trim());
    }
    if(uniqueAddonPriceIds.length){
        params.set('addon_price_ids', uniqueAddonPriceIds.join(','));
    }

    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
};

const EmailVerify = ({verify=false}) => {

    //Custom Hooks
    const navigate = useNavigate();
    const {user, isAuthenticating} = useUserContext();
    const [searchParams] = useSearchParams();
    const [token, setToken] = useState(null);

    const [errorMsg, setErrorMsg] = useState(false);
    const [successMsg, setSuccessMsg] = useState(false);
    
    const [continueToCheckout, setContinueToCheckout] = useState(false);
    const [autoLoginRedirectTriggered, setAutoLoginRedirectTriggered] = useState(false);

    //UseEffect Hooks
    useEffect(() => {
        //console.log("SETUSER", user);
        if(!verify && !user && !isAuthenticating){
            console.log("No user in email verify page", user, isAuthenticating);
            navigate(`/login/`, { replace: true });
            return;
        }
        const tokenParam = searchParams ? searchParams.get('token') : null;
        if (tokenParam) {
            setToken(tokenParam);
        } else {
            setToken(null);
        }

        if (user && user.settings?.subscribeTo?.planId) {
            setContinueToCheckout(user.settings.subscribeTo);
            document.location.href=`/signup/${user.settings.subscribeTo.planId}/${user.settings.subscribeTo.provider}${buildSubscribeToQuerySuffix(user.settings.subscribeTo)}`;
            return;
        }
        if (verify && tokenParam && !autoLoginRedirectTriggered) {
            setAutoLoginRedirectTriggered(true);
            document.location.href = `/login/email/callback?token=${encodeURIComponent(tokenParam)}`;
            return;
        }
    }, [user, verify, searchParams, autoLoginRedirectTriggered, navigate, isAuthenticating]);



    const handleResendEmail = async () => {
        setErrorMsg(false);
        setSuccessMsg(false);
        
        try{
            await resendVerificationEmail();
            //console.log("verification email resend");
            setSuccessMsg("Verification Email Sent!");
            setErrorMsg(false);
        } catch (err) {
            console.log("Verification email failed", err.error?err.error:err);
            setErrorMsg("Verification Email failed");
            setSuccessMsg(false);
        }    

    };

    const shouldRender = (block) => {
        switch(block){
            case "resend":
                return user && !user.email_verified;
            case "thanks":
                return (user && user.email_verified && verify) || (token && verify);
            case "checkout":
                return user && user.email_verified && verify && continueToCheckout;
            case "verifying":
                return !user && verify && continueToCheckout;
            case "loading":
                return !user && !verify;
            case "missingToken":
                return verify && !token && !isAuthenticating && !continueToCheckout;
            default:
                return false;
        }
    };

    return (
        <div className="layout">
            <Header search={() => null} />
            <div className="email-verify" >
                {shouldRender('resend') &&
                <div className="form" >
                    <div className="title m-b-16">Re-send Verification Email</div>
                    <div className="explanation big m-b-12">Your Email: {user.email}</div>
                    <div className="explanation m-b-12">Can't find your verification email? Click below to resend it.</div>
                    {errorMsg && 
                        <div className={`explanation error-msg m-b-12`}>{errorMsg}</div>
                    }
                    {successMsg && 
                        <div className={`explanation success-msg m-b-12`}>{successMsg}</div>
                    }
                    <ButtonCTA label="Re-send Email Verification" labelLoading="Sending..." onClick={handleResendEmail} />
                </div>
                }
                {shouldRender('thanks') &&
                <div className="form" >
                    <div className="title m-b-16">Thanks for verifying your email!</div>
                    {token && 
                        <ButtonCTA label="Start now" onClick={()=> document.location.href=`/login/email/callback?token=${encodeURIComponent(token)}`} />
                    }
                    {!token &&
                        <ButtonCTA label="Continue to Login" onClick={()=> document.location.href='/login'} />
                    }
                </div>
                }
                {shouldRender('missingToken') &&
                <div className="form" >
                    <div className="title m-b-16">Verification link incomplete</div>
                    <div className="explanation m-b-16">This link does not include a verification token. Please log in to continue.</div>
                    <ButtonCTA label="Go to Login" onClick={()=> document.location.href='/login'} />
                </div>
                }
                {shouldRender('checkout') &&
                <div className="form" >
                    <div className="title m-b-8">Last step! complete your checkout</div>
                    <div className="explanation m-b-16">If you are not automatically redirected to Checkout, click below:</div>
                    <ButtonCTA 
                        label="Go to Checkout" 
                        onClick={()=> document.location.href=`/signup/${continueToCheckout.planId}/${continueToCheckout.provider}${buildSubscribeToQuerySuffix(continueToCheckout)}`} 
                    />
                </div>
                }
                {shouldRender('verifying') &&
                    <Spinner label={"Verifying educational user..."} />
                }
                {shouldRender('loading') &&
                    <Spinner />
                }

            </div>
        </div>
    );
};

export default EmailVerify;
